import { useGoogleLogin } from '@react-oauth/google';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import { GOOGLE_CLIENT_ID } from '../config/googleOAuthConfig';
import { socialLogin } from '../store/slices/authSlice';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Container, Form, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  loginUser,
  clearError,
  clearOtpState,
  requestMobileOtp,
  verifyMobileOtp,
} from '../store/slices/authSlice';
import AuthMethodToggle from '../components/auth/AuthMethodToggle';
import AuthShowcase from '../components/auth/AuthShowcase';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';
import { ROUTES } from '../config/appConfig';
import '../styles/Auth.css';

const PHONE_REGEX = /^[0-9]{10}$/;
const OTP_REGEX = /^[0-9]{6}$/;

const validationSchema = Yup.object().shape({
  email: Yup.string().trim().required('Email, phone or username is required'),
  password: Yup.string().min(8, 'Use at least eight characters').required('Password is required'),
});

const showcaseFeatures = [
  {
    title: 'Clear action flow',
    description: 'Accept order, navigate, verify pickup OTP, and finish delivery in sequence.',
  },
  {
    title: 'Protected customer contact',
    description: 'Use masked calling for privacy without losing delivery coordination.',
  },
  {
    title: 'Live order queue',
    description: 'See available orders and active jobs in a cleaner dashboard.',
  },
];

const DriverLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, error, isAuthenticated, otpRequested, otpRequestMeta } = useSelector((state) => state.auth);
  const [loginMethod, setLoginMethod] = React.useState('email');
  const [mobileForm, setMobileForm] = React.useState({ phone: '', otp: '' });
  const [mobileError, setMobileError] = React.useState('');
  const googleLogin = useGoogleLogin({
    onSuccess: async ({ access_token }) => {
      setMobileError('');
      const result = await dispatch(socialLogin({ provider: 'google', token: access_token, role: 'driver' }));
      if (!result.error) navigate(ROUTES.driverDashboard);
    },
    onError: () => setMobileError('Google sign-in was cancelled or failed. Please try again.'),
  });
  const handleGoogleLogin = () => {
    if (!GOOGLE_CLIENT_ID) {
      setMobileError('Google sign-in is not configured.');
      return;
    }
    googleLogin();
  };
  const [showForgotPassword, setShowForgotPassword] = React.useState(false);

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(ROUTES.driverDashboard);
    }
  }, [isAuthenticated, navigate]);

  React.useEffect(() => {
    dispatch(clearOtpState());
  }, [dispatch]);

  const handleDismissError = () => {
    setMobileError('');
    dispatch(clearError());
  };

  const handleLoginMethodChange = (method) => {
    setLoginMethod(method);
    setMobileError('');
    dispatch(clearError());
    dispatch(clearOtpState());
    setMobileForm({ phone: '', otp: '' });
  };

  const validatePhone = () => {
    if (!PHONE_REGEX.test(mobileForm.phone)) {
      setMobileError('Enter a valid 10-digit mobile number.');
      return false;
    }

    return true;
  };

  const resetDriverOtpFlow = () => {
    setMobileForm((current) => ({ ...current, otp: '' }));
    setMobileError('');
    dispatch(clearOtpState());
  };

  const handleRequestOtp = async () => {
    if (loading || !validatePhone()) return;
    setMobileForm(current => ({ ...current, otp: '' }));
    dispatch(clearOtpState());
    setMobileError('');
    await dispatch(
      requestMobileOtp({
        phone: mobileForm.phone,
        purpose: 'LOGIN',
        role: 'driver',
      })
    );
  };

  const handleVerifyOtp = async () => {
    if (!validatePhone()) {
      return;
    }

    if (loading) return;
    if (!otpRequestMeta?.requestToken) { setMobileError('Request a mobile code first.'); return; }
    if (!OTP_REGEX.test(mobileForm.otp)) {
      setMobileError('Enter the 6-digit OTP sent to your mobile number.');
      return;
    }

    setMobileError('');
    const result = await dispatch(
      verifyMobileOtp({
        phone: mobileForm.phone,
        otp: mobileForm.otp,
        purpose: 'LOGIN',
        role: 'driver',
        requestToken: otpRequestMeta?.requestToken,
        otpToken: otpRequestMeta?.otpToken,
      })
    );

    if (result.payload && !result.error) {
      navigate(ROUTES.driverDashboard);
    }
  };

  return (
    <div className="auth-page driver-auth-page">
      <Container>
        <Row className="justify-content-center align-items-center min-vh-100">
          <Col xs={12} xl={11}>
            <div className="auth-layout auth-layout-driver">
              <AuthShowcase
                variant="driver"
                platformLabel="Driver Operations Platform"
                kicker="Driver Access"
                title="Move faster from pickup queue to completed drop."
                description="Accept jobs, verify pickup OTP, call customers safely, and complete deliveries without jumping between screens."
                features={showcaseFeatures}
              />

              <div className="auth-card auth-form-card p-4 rounded shadow">
              <div className="auth-heading-block">
                <span className="auth-chip driver-chip-auth">Driver Portal</span>
                <h2 className="mb-2 fw-bold">Sign in and start your route</h2>
                <p className="text-muted mb-0">Sign in with Google, email and password, or a mobile OTP.</p>
              </div>

              <GoogleSignInButton onClick={handleGoogleLogin} disabled={loading} />
              <AuthMethodToggle
                activeMethod={loginMethod}
                ariaLabel="Driver login method selector"
                onChange={handleLoginMethodChange}
              />


              {location.state?.message && <Alert variant="success">{location.state.message}</Alert>}
              {(error || mobileError) && (
                <Alert variant="danger" dismissible onClose={handleDismissError}>
                  {mobileError || error}
                </Alert>
              )}

              {loginMethod === 'mobile' && otpRequestMeta?.message && otpRequested && (
                <Alert variant="success" dismissible onClose={resetDriverOtpFlow}>
                  {otpRequestMeta.message}
                </Alert>
              )}

              {loginMethod === 'email' ? (
              <Formik
                initialValues={{ email: '', password: '' }}
                validationSchema={validationSchema}
                onSubmit={async (values) => {
                  const result = await dispatch(loginUser({ ...values, role: 'driver' }));
                  if (result.payload && !result.error) {
                    navigate(ROUTES.driverDashboard);
                  }
                }}
              >
                {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email, phone, or username</Form.Label>
                      <Form.Control
                        type="text" autoComplete="username"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && !!errors.email}
                        placeholder="you@example.com"
                      />
                      <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label>Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.password && !!errors.password}
                        placeholder="Enter your password"
                      />
                      <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                    </Form.Group>

                    <Button
                      variant="link"
                      type="button"
                      className="auth-inline-link d-block ms-auto px-0 mb-3"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot password?
                    </Button>

                    <Button variant="primary" type="submit" className="w-100 mb-3" disabled={loading}>
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Signing in...
                        </>
                      ) : (
                        'Sign in with password'
                      )}
                    </Button>
                  </Form>
                )}
              </Formik>
              ) : (
                <div className="otp-auth-panel">
                  <Form.Group className="mb-3">
                    <Form.Label>Mobile Number</Form.Label>
                    <Form.Control
                      type="tel"
                      value={mobileForm.phone}
                      onChange={(event) => {
                        setMobileForm((current) => ({
                          ...current,
                          phone: event.target.value.replace(/\D/g, '').slice(0, 10),
                        }));
                        dispatch(clearOtpState());
                        setMobileForm(current => ({ ...current, otp: '' }));
                        setMobileError('');
                      }}
                      placeholder="9876543210"
                      disabled={loading}
                    />
                  </Form.Group>

                  {otpRequested && (
                    <Form.Group className="mb-3">
                      <Form.Label>OTP</Form.Label>
                      <Form.Control
                        type="text"
                        value={mobileForm.otp}
                        onChange={(event) => {
                          setMobileForm((current) => ({
                            ...current,
                            otp: event.target.value.replace(/\D/g, '').slice(0, 6),
                          }));
                          setMobileError('');
                        }}
                        placeholder="Enter 6-digit OTP"
                        disabled={loading}
                      />
                    </Form.Group>
                  )}

                  <div className="otp-action-row mb-3">
                    {!otpRequested ? (
                      <Button variant="primary" className="w-100" onClick={handleRequestOtp} disabled={loading}>
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Sending OTP...
                          </>
                        ) : (
                          'Send Driver OTP'
                        )}
                      </Button>
                    ) : (
                      <>
                        <Button variant="primary" className="w-100" onClick={handleVerifyOtp} disabled={loading}>
                          {loading ? (
                            <>
                              <Spinner size="sm" className="me-2" />
                              Verifying...
                            </>
                          ) : (
                            'Sign in with mobile OTP'
                          )}
                        </Button>
                        <Button variant="outline-secondary" className="w-100" onClick={handleRequestOtp} disabled={loading}>
                          Resend OTP
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}

              <p className="text-center">
                Don't have an account? <Link to={ROUTES.driverSignup}>Sign Up as Driver</Link>
              </p>
              <p className="text-center">
                <Link to={ROUTES.home}>Back to welcome</Link>
              </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
      <ForgotPasswordModal
        show={showForgotPassword}
        onHide={() => setShowForgotPassword(false)}
        role="driver"
      />
    </div>
  );
};

export default DriverLogin;
